// src/pages/LettersPage.js
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function LettersPage() {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLetter, setEditingLetter] = useState(null);

  const loadLetters = () => {
    api.get('/letters').then(r => setLetters(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { loadLetters(); }, []);

  const handleDelete = async (letter) => {
    if (!window.confirm(`Delete letter "${letter.subject}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/letters/${letter.id}`);
      toast.success('Letter deleted.');
      loadLetters();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete letter.');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Letters</h1>
          <p className="page-subtitle">AI-powered letter generator</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>✍ Generate Letter</button>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Subject</th><th>Business</th><th>Contact</th><th>AI</th><th>Status</th><th>Created</th><th style={{ width: 90 }}></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></td></tr>
            : letters.length === 0 ? <tr><td colSpan={7}><div className="empty-state"><div className="empty-state-icon">✍️</div><h3>No letters yet</h3></div></td></tr>
            : letters.map(l => (
              <tr key={l.id}>
                <td className="td-primary">{l.subject}</td>
                <td style={{ fontSize: '0.875rem' }}>{l.business_name || '—'}</td>
                <td style={{ fontSize: '0.875rem' }}>{l.contact_first_name ? `${l.contact_first_name} ${l.contact_last_name}` : '—'}</td>
                <td>{l.ai_generated ? '🤖' : '—'}</td>
                <td><span className={`badge badge-${l.status}`}>{l.status}</span></td>
                <td style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{fmtDate(l.created_at)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {l.status !== 'sent' && (
                      <button
                        className="btn btn-ghost btn-icon"
                        onClick={() => setEditingLetter(l)}
                        title="Edit letter"
                      >✎</button>
                    )}
                    {l.status !== 'sent' && (
                      <button
                        className="btn btn-ghost btn-icon"
                        onClick={() => handleDelete(l)}
                        title="Delete letter"
                        style={{ color: 'var(--danger)' }}
                      >✕</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && <GenerateLetterModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); loadLetters(); }} />}
      {editingLetter && <EditLetterModal letter={editingLetter} onClose={() => setEditingLetter(null)} onSaved={() => { setEditingLetter(null); loadLetters(); }} />}
    </div>
  );
}

function EditLetterModal({ letter, onClose, onSaved }) {
  const [form, setForm] = useState({
    subject:  letter.subject || '',
    bodyHtml: letter.body_html || '',
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // List endpoint omits body_html — fetch the full record on open
  useEffect(() => {
    api.get(`/letters/${letter.id}`)
      .then(res => {
        setForm({
          subject:  res.data.subject || '',
          bodyHtml: res.data.body_html || '',
        });
      })
      .catch(err => {
        console.error('Failed to load letter:', err);
        toast.error('Failed to load letter content.');
      })
      .finally(() => setLoading(false));
  }, [letter.id]);

  const handleSave = async () => {
    if (!form.subject.trim()) return toast.error('Subject is required.');
    setSaving(true);
    try {
      await api.patch(`/letters/${letter.id}`, form);
      toast.success('Letter updated.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update letter.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Edit Letter</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Subject *</label>
                <input className="form-input" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Body (HTML supported)</label>
                <textarea className="form-input" rows={14} value={form.bodyHtml} onChange={e => setForm(f => ({ ...f, bodyHtml: e.target.value }))} style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

function GenerateLetterModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ letterType: 'welcome', context: '', subject: '', additionalInstructions: '' });
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null);
  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const handleGenerate = async () => {
    if (!form.context) return toast.error('Please provide context for the letter.');
    setLoading(true);
    try {
      const res = await api.post('/letters/generate', form);
      setGenerated(res.data);
      toast.success('Letter generated!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Generation failed.');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">AI Letter Generator</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {!generated ? (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Letter Type</label>
                  <select className="form-select" value={form.letterType} onChange={e => set('letterType', e.target.value)}>
                    <option value="welcome">Welcome Letter</option>
                    <option value="renewal_reminder">Renewal Reminder</option>
                    <option value="thank_you">Thank You</option>
                    <option value="event_invitation">Event Invitation</option>
                    <option value="overdue">Overdue / Lapsed</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Subject Line</label>
                  <input className="form-input" value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Optional — auto-generated if blank" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Context *</label>
                <textarea className="form-textarea" rows={4} value={form.context} onChange={e => set('context', e.target.value)} placeholder="Describe what the letter should cover. E.g. 'Thank them for attending the Annual Gala and mention we hope to see them at the upcoming Mission to DC event on July 15.'" />
              </div>
              <div className="form-group">
                <label className="form-label">Additional Instructions</label>
                <input className="form-input" value={form.additionalInstructions} onChange={e => set('additionalInstructions', e.target.value)} placeholder="Any specific tone, length, or formatting notes..." />
              </div>
              <button className="btn btn-primary w-full" style={{ justifyContent: 'center', marginTop: 4 }} onClick={handleGenerate} disabled={loading}>
                {loading ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Generating...</> : '🤖 Generate with AI'}
              </button>
            </>
          ) : (
            <div>
              <div style={{ marginBottom: 12 }}>
                <label className="form-label">Generated Letter</label>
                <div style={{ border: '1px solid var(--gray-200)', borderRadius: 'var(--radius)', padding: 16, background: 'var(--gray-50)', minHeight: 200 }} dangerouslySetInnerHTML={{ __html: generated.body_html }} />
                <p className="form-hint" style={{ marginTop: 8, color: 'var(--success)' }}>
                  ✓ Letter automatically saved as draft. You can edit it from the Letters list.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setGenerated(null)}>← Generate Again</button>
                <button className="btn btn-primary btn-sm" onClick={onSaved}>Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
